import path from 'path';
import { readdir } from 'fs/promises';
import Debug from './debug.js';
import micromatch from 'micromatch';
import { cl, pr, pp } from './print.js';
import descriptions from './descriptions.js';

const debug = Debug('utils.js');

const makeSetFromArray = (arr) => {
  const result = new Set();
  arr.forEach((item) => result.add(item));
  return result;
};

/**
 * Get list of files in given folder and its subfolders that match against a pattern.
 * ex: await getSubFolderFiles(process.cwd(), ['!d/sync-output', '!d/etl-output', '!d/snapshots']);
 * @param {string} dir starting directory
 * @param {Array<string>} pattern The match patterns. ex: '!my_folder', 'd/uploads', '!f/exclude.txt'
 * @returns {Array<string>} All file paths that meet the pattern criteria
 */
const getSubFolderFiles = async (dir, pattern) => {
  const matches = {
    excludedDirs: new Set(),
    excludedFiles: new Set(),
    includedDirs: new Set(),
    includedFiles: new Set(),
  };

  if (pattern && pattern.length > 0) {
    const excluded = [];
    const included = [];
    pattern.forEach((item) => {
      if (item.startsWith('!')) excluded.push(item.substring(1));
      else included.push(item);
    });
    included.forEach((item) => {
      if (item.startsWith('d/')) matches.includedDirs.add(item.substring(2));
      else if (item.startsWith('f/')) matches.includedFiles.add(item.substring(2));
      else {
        matches.includedDirs.add(item);
        matches.includedFiles.add(item);
      }
    });
    excluded.forEach((item) => {
      if (item.startsWith('d/')) matches.excludedDirs.add(item.substring(2));
      else if (item.startsWith('f/')) matches.excludedFiles.add(item.substring(2));
      else {
        matches.excludedDirs.add(item);
        matches.excludedFiles.add(item);
      }
    });
  }

  return _getSubFolderFiles(dir, matches);
};

const _getSubFolderFiles = async (dir, matches) => {
  const files = await readdir(dir, { withFileTypes: true });

  const result = [];
  for (const file of files) {
    if (!file.isSymbolicLink()) {
      if (file.isFile() && (!matches.excludedFiles.has(file.name) || matches.includedFiles.has(file.name)))
        result.push(path.resolve(dir, file.name));
      else if (file.isDirectory() && (!matches.excludedDirs.has(file.name) || matches.includedFiles.has(file.name))) {
        const sub = await _getSubFolderFiles(path.resolve(dir, file.name), matches);
        if (sub.length > 0) result.push(...sub);
      }
    }
  }

  return result;
};

const _getFolderFiles = async (dir, options, result = []) => {
  const { includeSymLinks, recursive, filter } = options;
  const { matcher } = filter;

  const fullPath = path.resolve(dir);
  const files = await readdir(fullPath, { withFileTypes: true });

  for (const file of files) {
    const filePath = path.resolve(fullPath, file.name);

    if (file.isSymbolicLink() && !includeSymLinks) {
      continue;
    }

    if (matcher && !matcher(filePath)) {
      debug('skip', filePath);
      continue;
    }

    if (file.isFile()) {
      result.push(path.resolve(fullPath, file.name));
    } else if (file.isDirectory() && recursive) {
      await _getFolderFiles(path.resolve(fullPath, file.name), options, result);
    }
  }

  return result;
};

const getFolderFiles = async (dir, options) => {
  const _options = { includeSymLinks: false, recursive: false, filter: {}, ...options };
  const { pattern } = _options.filter;

  if (pattern) {
    _options.filter.matcher = micromatch.matcher(pattern, { dot: true });
  }

  return _getFolderFiles(dir, _options);
};

const filterFiles = (files, filter) => {
  debug('filter-in', files);
  const { pattern } = { ...filter };

  let result = files;
  if (pattern) result = micromatch(result, pattern);
  debug('filter-out', result);

  return result;
};

/**
 * todo: implement my variant:
 * - explicit f/ and d/ prefixes
 * -
 * @param {*} pattern
 * @returns a matcher instance
 */
function createMatcher(pattern) {
  const matches = {
    excludedDirs: new Set(),
    excludedFiles: new Set(),
    includedDirs: new Set(),
    includedFiles: new Set(),
  };
  const excluded = [];
  const included = [];
  pattern.forEach((item) => {
    if (item.startsWith('!')) excluded.push(item.substring(1));
    else included.push(item);
  });
  included.forEach((item) => {
    if (item.startsWith('d/')) matches.includedDirs.add(item.substring(2));
    else if (item.startsWith('f/')) matches.includedFiles.add(item.substring(2));
    else {
      matches.includedDirs.add(item);
      matches.includedFiles.add(item);
    }
  });
  excluded.forEach((item) => {
    if (item.startsWith('d/')) matches.excludedDirs.add(item.substring(2));
    else if (item.startsWith('f/')) matches.excludedFiles.add(item.substring(2));
    else {
      matches.excludedDirs.add(item);
      matches.excludedFiles.add(item);
    }
  });

  const match = (path) => {};

  return { match };
}

function userSetOption(option) {
  function wasArgPassed(option) {
    return process.argv.indexOf(option) > -1;
  }

  if (wasArgPassed(`--${option}`)) {
    return true;
  }

  // Handle aliases for same option
  const aliases = descriptions.options[option]?.config?.alias || [];
  for (let i in aliases) {
    if (wasArgPassed(`-${aliases[i]}`)) return true;
  }

  return false;
}

const streamToString = (stream) => {
  const chunks = [];
  return new Promise((resolve, reject) => {
    stream.on('error', reject);
    stream.on('data', (c) => chunks.push(Buffer.from(c)));
    stream.on('end', () => resolve(Buffer.concat(chunks).toString('utf8')));
  });
};

const buildChunks = (array, chunkSize = 10) => {
  const resultArray = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    const chunk = array.slice(i, i + chunkSize);
    resultArray.push(chunk);
  }
  return resultArray;
};

export { makeSetFromArray, getFolderFiles, filterFiles, userSetOption, streamToString, buildChunks };
